import { Grid, TextInput } from '@mantine/core'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreCountryCode, type CoreUserAddress } from '@flecto/types'
import { AutocompleteAddress, SelectCountry } from '@flecto/ui'
import { useGetCountryName, useValidateCityPostal } from '@flecto/utils'

import { useOnboardingNavigation } from '../../hooks/useOnboardingNavigation'
import { useFormContext } from './FormOnboarding'
import { StepWrapper } from './StepWrapper'

export function Step02() {
  const [addressSelected, setAddressSelected] = useState(false)
  const [cityEditable, setCityEditable] = useState(false)
  const [postalCodeEditable, setPostalCodeEditable] = useState(false)
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const { nextStep } = useOnboardingNavigation()

  const form = useFormContext()
  const getCountryName = useGetCountryName()

  const addressStreet = form.values.step_location.address.address_street
  const city = form.values.step_location.address.address_city
  const postalCode = form.values.step_location.address.address_postal_code

  const canProceed = !!addressStreet && !!city && !!postalCode

  useEffect(() => {
    if (!addressStreet) {
      setAddressSelected(false)
      setCityEditable(false)
      setPostalCodeEditable(false)
      form.setFieldValue('step_location.address.address_city', '')
      form.setFieldValue('step_location.address.address_postal_code', '')
      form.setFieldValue('step_location.address.fromGoogle', false)
    }
  }, [addressStreet])
  const handleNext = async () => {
    const fromGoogle = form.values.step_location.address.fromGoogle
    form.validate()

    const country = form.values.step_location.address
      .address_country_code as CoreCountryCode
    const postalCode = form.values.step_location.address
      .address_postal_code as string
    const city = form.values.step_location.address.address_city as string
    if (!fromGoogle) {
      const { isValid, countryMatches, postalMatches } =
        await useValidateCityPostal({
          city,
          postalCode,
          countryCode: country,
        })

      if (!isValid) {
        if (!countryMatches) {
          form.setFieldError(
            'step_location.address.address_city',
            t('form_placeholders.cityPostalMismatch')
          )
        }
        if (!postalMatches) {
          form.setFieldError(
            'step_location.address.address_postal_code',
            t('form_placeholders.cityPostalMismatch')
          )
        }
        return
      }
    }
    const isFormValid =
      form.isValid('step_location.address.address_country_code') &&
      form.isValid('step_location.address.address_postal_code') &&
      form.isValid('step_location.address.address_street') &&
      form.isValid('step_location.address.address_city') &&
      form.isValid('step_location.address.address_region')

    if (!isFormValid) {
      return
    }

    form.clearErrors()
    nextStep()
  }

  const countryOfOperations = useMemo(() => {
    return form.values.step_location?.address?.address_country_code ?? 'PT'
  }, [form.values])

  const previousCountryRef = useRef<CoreCountryCode | undefined>(undefined)

  useEffect(() => {
    const previousCountry = previousCountryRef.current
    const currentCountry =
      form.values.step_location.address.address_country_code

    if (
      previousCountry &&
      previousCountry !== currentCountry &&
      form.values.step_location.address.address_street
    ) {
      form.setFieldValue('step_location.address.address_street', '')
      form.setFieldValue('step_location.address.address_city', '')
      form.setFieldValue('step_location.address.address_postal_code', '')
      form.setFieldValue('step_location.address.fromGoogle', false)
      setAddressSelected(false)
      setCityEditable(false)
      setPostalCodeEditable(false)
    }

    previousCountryRef.current = currentCountry
  }, [form.values.step_location.address.address_country_code])

  useEffect(() => {
    const currentValue = form.getInputProps(
      'step_location.address.address_street'
    ).value

    if (
      addressSelected &&
      currentValue !== form.values.step_location.address.last_selected
    ) {
      setAddressSelected(false)
      setCityEditable(false)
      setPostalCodeEditable(false)
      form.setFieldValue('step_location.address.address_city', '')
      form.setFieldValue('step_location.address.address_postal_code', '')
      form.setFieldValue('step_location.address.fromGoogle', false)
    }
  }, [form.getInputProps('step_location.address.address_street').value])

  const selectAddress = (address: CoreUserAddress) => {
    form.setFieldValue(
      'step_location.address.fromGoogle',
      Boolean(address.address_city && address.address_postal_code)
    )

    setAddressSelected(true)
    form.setFieldValue(
      'step_location.address.last_selected',
      address.address_street
    )

    if (address.address_label) {
      form.setFieldValue(
        'step_location.address.address_street',
        `${address.address_label}, ${address.address_street}`
      )
    } else {
      form.setFieldValue(
        'step_location.address.address_street',
        address.address_street
      )
    }
    form.setFieldValue('step_location.address.latitude', address.latitude)
    form.setFieldValue('step_location.address.longitude', address.longitude)
    if (address.address_city) {
      form.setFieldValue(
        'step_location.address.address_city',
        address.address_city
      )
      setCityEditable(false)
    } else {
      form.setFieldValue('step_location.address.address_city', '')
      setCityEditable(true)
    }
    if (address.address_postal_code) {
      form.setFieldValue(
        'step_location.address.address_postal_code',
        address.address_postal_code
      )
      setPostalCodeEditable(false)
    } else {
      form.setFieldValue('step_location.address.address_postal_code', '')
      setPostalCodeEditable(true)
    }
    form.setFieldValue(
      'step_location.address.address_country_name',
      getCountryName(address.address_country_code)
    )
    form.setFieldValue(
      'step_location.address.address_country_code',
      address.address_country_code
    )
    form.setFieldValue(
      'step_location.address.address_region',
      address.address_region
    )
  }

  return (
    <StepWrapper
      stepNumber={2}
      title={t('steps.main_location.title')}
      description={t('steps.main_location.description')}
      onNextStep={handleNext}
      nextStepDisabled={!canProceed}
    >
      <SelectCountry
        {...form.getInputProps('step_location.address.address_country_code')}
        onChange={(countryCode) => {
          form.setFieldValue(
            'step_location.address.address_country_code',
            countryCode as CoreCountryCode
          )
          form.setFieldValue(
            'step_location.address.address_country_name',
            getCountryName(countryCode as CoreCountryCode)
          )
        }}
        label={t('form_labels.country_operation')}
        placeholder={t('form_placeholders.pick_one')}
        withAsterisk
        mb='md'
      />
      <AutocompleteAddress
        value={form.values.step_location.address.address_street}
        {...form.getInputProps('step_location.address.address_street')}
        withAsterisk
        mb='md'
        label={t('form_labels.street')}
        onSelectAddress={(address) => {
          const selectedCountry =
            form.values.step_location.address.address_country_code
          if (address.address_country_code !== selectedCountry) {
            form.setFieldError(
              'step_location.address.address_street',
              t('form_placeholders.addressCountryMismatch')
            )
            return
          }

          selectAddress(address)
        }}
        countryToSearch={countryOfOperations}
        description={t('form_placeholders.choose_address')}
      />

      <Grid mb='xl'>
        <Grid.Col span={8}>
          <TextInput
            {...form.getInputProps('step_location.address.address_city')}
            label={t('form_labels.city')}
            withAsterisk
            disabled={!cityEditable && addressSelected}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            {...form.getInputProps('step_location.address.address_postal_code')}
            label={t('form_labels.zip_code')}
            withAsterisk
            disabled={!postalCodeEditable && addressSelected}
          />
        </Grid.Col>
      </Grid>
    </StepWrapper>
  )
}
